import { useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/utils'
import type { AvatarSessionState } from '@/connectors/avatar'
import { Video, VideoOff, Maximize2, Minimize2 } from 'lucide-react'
import { Button, Spinner } from '@/components/ui'

type DisplaySize = 'small' | 'medium' | 'large' | 'fullscreen'

interface TavusAvatarDisplayProps {
  videoElement: HTMLVideoElement | null
  isReady: boolean
  sessionState: AvatarSessionState
  size?: DisplaySize
  onToggleFullscreen?: () => void
  isFullscreen?: boolean
  className?: string
}

const sizeClasses: Record<DisplaySize, string> = {
  small: 'w-48 h-36',
  medium: 'w-80 h-60',
  large: 'w-full max-w-2xl aspect-video',
  fullscreen: 'fixed inset-0 w-full h-full z-50',
}

export function TavusAvatarDisplay({
  videoElement,
  isReady,
  sessionState,
  size = 'large',
  onToggleFullscreen,
  isFullscreen = false,
  className,
}: TavusAvatarDisplayProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  // Mount video element when ready
  useEffect(() => {
    if (videoElement && containerRef.current && isReady) {
      // Clear previous content
      containerRef.current.innerHTML = ''

      // Clear any inline styles from provider setup (e.g. AnamSession hides
      // the element while the SDK attaches the WebRTC stream) and apply ours
      videoElement.removeAttribute('style')
      videoElement.className = 'w-full h-full object-cover rounded-xl'

      // Append to container
      containerRef.current.appendChild(videoElement)

      // Ensure video plays
      videoElement.play().catch((error) => {
        console.warn('Video autoplay was prevented:', error)
      })

      return () => {
        if (containerRef.current) {
          containerRef.current.innerHTML = ''
        }
      }
    }
  }, [videoElement, isReady])

  const isConnecting = sessionState === 'connecting'
  const isError = sessionState === 'error'
  const isSpeaking = sessionState === 'speaking'

  return (
    <div
      className={cn(
        'relative overflow-hidden bg-dark-900 rounded-xl',
        sizeClasses[size],
        isFullscreen && 'rounded-none',
        className
      )}
    >
      {/* Video container */}
      <div
        ref={containerRef}
        className={cn(
          'w-full h-full flex items-center justify-center',
          !isReady && 'hidden'
        )}
      />

      {/* Loading state */}
      <AnimatePresence>
        {isConnecting && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 flex flex-col items-center justify-center bg-dark-900/90"
          >
            <Spinner className="w-12 h-12 mb-4 text-dark-300" />
            <span className="text-dark-300">Connecting to avatar...</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Idle state */}
      {sessionState === 'idle' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <Video className="w-16 h-16 text-dark-600 mb-4" />
          <span className="text-dark-400">Avatar not connected</span>
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-destructive/10">
          <VideoOff className="w-16 h-16 text-destructive mb-4" />
          <span className="text-destructive">Connection failed</span>
        </div>
      )}

      {/* Speaking indicator */}
      <AnimatePresence>
        {isSpeaking && isReady && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="absolute bottom-4 left-1/2 -translate-x-1/2"
          >
            <div className="flex items-center gap-2 px-3 py-1.5 bg-black/60 rounded-full">
              <div className="flex items-center gap-1">
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    animate={{
                      scaleY: [1, 1.5, 1],
                    }}
                    transition={{
                      duration: 0.5,
                      repeat: Infinity,
                      delay: i * 0.15,
                    }}
                    className="w-1 h-3 bg-primary-500 rounded-full"
                  />
                ))}
              </div>
              <span className="text-xs text-white">Speaking</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fullscreen toggle */}
      {onToggleFullscreen && isReady && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleFullscreen}
          className="absolute top-2 right-2 bg-black/40 hover:bg-black/60 text-white"
        >
          {isFullscreen ? (
            <Minimize2 className="w-4 h-4" />
          ) : (
            <Maximize2 className="w-4 h-4" />
          )}
        </Button>
      )}
    </div>
  )
}
